import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
	createFileRoute,
	Outlet,
	useNavigate,
	useParams,
	useRouterState,
} from "@tanstack/react-router";
import { Copy01, Dataflow01, Expand03, Trash02 } from "@untitledui/icons";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BadgeWithDot } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { Dropdown } from "@/components/base/dropdown/dropdown";
import { Label } from "@/components/base/label";
import { EmptyValue } from "@/components/common/EmptyValue";
import CopyableId from "@/components/Molecules/CopyableId/CopyableId";
import type { ColumnDef } from "@/components/Molecules/Table";
import { CustomTableWrapper } from "@/components/Molecules/Table/CustomTableWrapper";
import { TableSearchEmptyState } from "@/components/Molecules/Table/TableSearchEmptyState";
import { getActionsColumnHeader } from "@/components/Molecules/Table/table-column-headers";
import { CreateLifecycleModal } from "@/components/Organisms/CreateLifecycleModal/CreateLifecycleModal";
import { DeleteLifecycleModal } from "@/components/Organisms/DeleteLifecycleModal/DeleteLifecycleModal";
import { PageHeader } from "@/components/Organisms/PageHeader/PageHeader";
import { PageLayout } from "@/components/Organisms/PageLayout/PageLayout";
import { statusLifecycleDisplayLabel } from "@/forms/ticket-type/create-ticket-type/ticket-type-lifecycle.utils";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useTableSettings } from "@/hooks/use-table-settings";
import { authQueryOptions } from "@/services/auth/auth.options";
import { useExportStatusLifecycles } from "@/services/lifecycle/lifecycle.mutations";
import {
	lifecycleKeys,
	lifecycleQueryOptions,
} from "@/services/lifecycle/lifecycle.options";
import {
	getEntityTypeClassLabel,
	type StatusLifecycleDisplay,
} from "@/services/lifecycle/lifecycle.types";
import { downloadBlob, generateExportFilename } from "@/utils/download";
import { isLifecycleActive } from "@/utils/lifecycle-admin";

export const Route = createFileRoute(
	"/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/configuration/lifecycles",
)({
	component: LifecyclesIndex,
});

const TABLE_KEY = "orgAdmin.configuration.lifecycles";

const DEFAULT_COLUMN_ORDER = [
	"name",
	"description",
	"source",
	"entityTypeClass",
	"state",
	"id",
	"actions",
];
const DEFAULT_SORTING = [{ id: "name", desc: false }];
const DEFAULT_PAGE_SIZE = 10;
const ACTIONS_COLUMN_ID = "actions";

const sortFieldMapping: Record<string, string[]> = {
	name: ["Name"],
	description: ["Description"],
	id: ["Id"],
	entityTypeClass: ["EntityTypeClass"],
	state: ["Active"],
};

/**
 * (kwt 05/20/26)
 * Persisted columnOrder from useTableSettings is not merged when new columns ship.
 * Merges saved order with DEFAULT_COLUMN_ORDER, backfills missing columns (e.g.
 * entityTypeClass), and keeps actions last. Same pattern may be needed on other
 * reorderable admin tables when DEFAULT_COLUMN_ORDER gains columns.
 */
const normalizeColumnOrder = (columnOrder?: string[]) => {
	const nonActionDefaultColumns = DEFAULT_COLUMN_ORDER.filter(
		(columnId) => columnId !== ACTIONS_COLUMN_ID,
	);

	if (!Array.isArray(columnOrder) || columnOrder.length === 0) {
		return [...nonActionDefaultColumns, ACTIONS_COLUMN_ID];
	}

	const allowedColumns = new Set(DEFAULT_COLUMN_ORDER);
	const sanitized = columnOrder.filter(
		(columnId) =>
			allowedColumns.has(columnId) && columnId !== ACTIONS_COLUMN_ID,
	);
	const unique = Array.from(new Set(sanitized));
	const missingDefaults = nonActionDefaultColumns.filter(
		(columnId) => !unique.includes(columnId),
	);

	return [...unique, ...missingDefaults, ACTIONS_COLUMN_ID];
};

function LifecycleDeleteAction({
	lc,
	onDelete,
}: {
	lc: StatusLifecycleDisplay;
	onDelete: () => void;
}) {
	const { t } = useAppTranslation();
	const canDelete = useQuery({
		...authQueryOptions.checkAction({
			name: "DeleteStatusLifecycle",
			entityType: "StatusLifecycle",
			entityId: lc.id,
		}),
	});

	if (!canDelete.data) return null;

	return (
		<>
			<Dropdown.Separator />
			<Dropdown.Item
				id="delete"
				label={t("admin.lifecycles.table.actions.delete")}
				icon={Trash02}
				className="[&_span]:text-red-600"
				onAction={onDelete}
			/>
		</>
	);
}

function LifecyclesIndex() {
	const { t } = useAppTranslation();
	const viewAllOrgsQuery = useQuery(
		authQueryOptions.checkAction({
			name: "ViewAllOrganizations",
			entityType: null,
			entityId: null,
		}),
	);
	const isPlatformAdmin = viewAllOrgsQuery.data ?? false;
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const pathname = useRouterState({ select: (s) => s.location.pathname });
	const isFullRoute = /\/configuration\/lifecycles\/[^/]+\/full/.test(pathname);
	const { orgId } = useParams({
		from: "/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/configuration/lifecycles",
	});
	const [isOpen, setIsOpen] = useState(false);
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
	const [pageIndex, setPageIndex] = useState(0);
	const [searchTerm, setSearchTerm] = useState("");
	const [isRefreshing, setIsRefreshing] = useState(false);
	const [tableOptionsHost, setTableOptionsHost] =
		useState<HTMLDivElement | null>(null);
	const [selectedLifecycle, setSelectedLifecycle] = useState<{
		id: string;
		name: string;
	} | null>(null);

	const [sortParams, setSortParams] = useState({
		"OrderBy.Fields": ["Name"] as string[],
		"OrderBy.Descending": false,
	});

	const hasAppliedPersistedSettings = useRef(false);

	const {
		settings: tableSettings,
		isLoaded: settingsLoaded,
		saveSorting,
		saveColumnOrder,
		saveColumnVisibility,
		saveSearchTerm,
		savePageSize,
	} = useTableSettings<Record<string, never>>({
		tableKey: TABLE_KEY,
		defaultSorting: DEFAULT_SORTING,
		defaultColumnOrder: DEFAULT_COLUMN_ORDER,
		defaultSearchTerm: "",
		defaultFilters: {},
		defaultPageSize: DEFAULT_PAGE_SIZE,
	});

	useEffect(() => {
		if (!settingsLoaded || hasAppliedPersistedSettings.current) return;
		hasAppliedPersistedSettings.current = true;
		if (tableSettings.searchTerm) setSearchTerm(tableSettings.searchTerm);
		if (
			Number.isFinite(tableSettings.pageSize) &&
			[10, 25, 50, 100].includes(tableSettings.pageSize)
		) {
			setPageSize(tableSettings.pageSize);
		}
	}, [settingsLoaded, tableSettings.searchTerm, tableSettings.pageSize]);

	const scopedBaseParams = useMemo(
		() => ({
			organizationId: orgId,
		}),
		[orgId],
	);

	const searchTrimmed = searchTerm.trim();

	const mergedServerParams = useMemo(
		() => ({
			...scopedBaseParams,
			...sortParams,
			...(searchTrimmed ? { SearchTerm: searchTrimmed } : {}),
		}),
		[scopedBaseParams, sortParams, searchTrimmed],
	);

	const apiStart = pageIndex * pageSize + 1;

	const countQuery = useQuery({
		...lifecycleQueryOptions.statusLifecyclesCount(mergedServerParams),
	});

	const unfilteredCountQuery = useQuery({
		...lifecycleQueryOptions.statusLifecyclesCount(scopedBaseParams),
	});

	const pageQuery = useQuery({
		...lifecycleQueryOptions.statusLifecyclesPage(
			apiStart,
			pageSize,
			mergedServerParams,
		),
	});

	const exportMutation = useExportStatusLifecycles();

	const canCreateLifecycle = useQuery({
		...authQueryOptions.checkAction({
			name: "CreateStatusLifecycle",
			entityId: orgId,
		}),
	});

	const data = useMemo(() => pageQuery.data ?? [], [pageQuery.data]);

	const pageCount = useMemo(() => {
		const total = countQuery.data ?? 0;
		if (!total) return 0;
		return Math.ceil(total / pageSize);
	}, [countQuery.data, pageSize]);

	useEffect(() => {
		const total = countQuery.data ?? 0;
		if (!total) return;
		const pages = Math.max(1, Math.ceil(total / pageSize));
		if (pageIndex > 0 && pageIndex >= pages) {
			setPageIndex(Math.max(0, pages - 1));
		}
	}, [countQuery.data, pageSize, pageIndex]);

	useEffect(() => {
		if (!pageCount) return;
		const prefetchRange = 1;
		for (let i = 1; i <= prefetchRange; i++) {
			const nextPageIndex = pageIndex + i;
			if (nextPageIndex < pageCount) {
				const nextStart = nextPageIndex * pageSize + 1;
				void queryClient.prefetchQuery(
					lifecycleQueryOptions.statusLifecyclesPage(
						nextStart,
						pageSize,
						mergedServerParams,
					),
				);
			}
		}
		for (let i = 1; i <= prefetchRange; i++) {
			const prevPageIndex = pageIndex - i;
			if (prevPageIndex >= 0) {
				const prevStart = prevPageIndex * pageSize + 1;
				void queryClient.prefetchQuery(
					lifecycleQueryOptions.statusLifecyclesPage(
						prevStart,
						pageSize,
						mergedServerParams,
					),
				);
			}
		}
	}, [pageIndex, pageSize, pageCount, mergedServerParams, queryClient]);

	const handleRefresh = async () => {
		setIsRefreshing(true);
		try {
			await queryClient.invalidateQueries({ queryKey: lifecycleKeys.all });
		} finally {
			setIsRefreshing(false);
		}
	};

	const columnLabels = useMemo(
		() => ({
			name: t("admin.lifecycles.table.columns.name"),
			description: t("admin.lifecycles.table.columns.description"),
			source: t("admin.lifecycles.table.columns.source"),
			entityTypeClass: t("admin.lifecycles.table.columns.entityType"),
			state: t("admin.lifecycles.table.columns.state"),
			id: t("admin.lifecycles.table.columns.id"),
		}),
		[t],
	);

	const handleExport = useCallback(
		(visibleColumns?: string[]) => {
			const totalCount = countQuery.data ?? 0;
			if (totalCount === 0 || totalCount > 10000) return;

			const allIds = DEFAULT_COLUMN_ORDER;
			const ids = (visibleColumns?.length ? visibleColumns : allIds).filter(
				(c) => c !== "actions",
			);

			const apiColumnById: Record<string, string> = {
				name: "Name",
				description: "Description",
				source: "Source",
				entityTypeClass: "EntityTypeClass",
				state: "Active",
				id: "Id",
			};
			const apiColumns = ids
				.map((c) => apiColumnById[c])
				.filter(Boolean)
				.join(",");

			exportMutation.mutate(
				{
					startIndex: 1,
					count: totalCount,
					params: {
						...mergedServerParams,
						...(apiColumns ? { columns: apiColumns } : {}),
					},
				},
				{
					onSuccess: (blob) => {
						downloadBlob(blob, generateExportFilename("lifecycles"));
					},
				},
			);
		},
		[countQuery.data, exportMutation, mergedServerParams],
	);

	const navigateFullPage = useCallback(
		(lifecycleId: string) => {
			navigate({
				to: `/org-admin/${orgId}/configuration/lifecycles/${lifecycleId}/full`,
			});
		},
		[navigate, orgId],
	);

	const handleRowClick = (row: StatusLifecycleDisplay) => {
		navigate({
			to: "/org-admin/$orgId/configuration/lifecycles/$lifecycleId/details",
			params: { orgId, lifecycleId: row.id },
			search: { mode: "view" },
		});
	};

	const handlePaginationChange = ({
		pageSize: newPageSize,
		pageIndex: newPageIndex,
	}: {
		pageSize: number;
		pageIndex: number;
	}) => {
		if (newPageSize !== pageSize) {
			setPageSize(newPageSize);
			setPageIndex(0);
			savePageSize(newPageSize);
		} else if (newPageIndex !== pageIndex) {
			setPageIndex(newPageIndex);
		}
	};

	const onSortChange = useCallback(
		({ fields, descending }: { fields: string[]; descending: boolean }) => {
			setSortParams({
				"OrderBy.Fields": fields,
				"OrderBy.Descending": descending,
			});
		},
		[],
	);

	const lifecycleColumns: ColumnDef<StatusLifecycleDisplay>[] = useMemo(
		() => [
			{
				id: "name",
				accessorFn: (row) => statusLifecycleDisplayLabel(row),
				header: columnLabels.name,
				enableSorting: true,
				meta: {
					headerProps: { isRowHeader: true },
				},
				minSize: 280,
				cell: ({ row }) => {
					const lc = row.original;
					return (
						<div className="flex items-center gap-3">
							<div className="flex size-8 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-gray-100">
								<Dataflow01 className="size-4 text-gray-500" aria-hidden />
							</div>
							<div className="min-w-0">
								<p className="text-sm font-medium text-primary">
									{statusLifecycleDisplayLabel(lc)}
								</p>
							</div>
						</div>
					);
				},
			},
			{
				id: "description",
				accessorFn: (row) => row.description ?? "",
				header: columnLabels.description,
				enableSorting: true,
				minSize: 220,
				cell: ({ row }) => {
					const d = row.original.description;
					return d ? (
						<p className="text-sm font-medium text-primary">{d}</p>
					) : (
						<EmptyValue />
					);
				},
			},
			{
				id: "source",
				accessorFn: (row) => (row.isDefault === true ? "system" : "custom"),
				header: columnLabels.source,
				enableSorting: false,
				minSize: 120,
				cell: ({ row }) => (
					<Label size="md" type="dark" showIcon={false}>
						{row.original.isDefault === true
							? t("admin.lifecycles.table.source.system")
							: t("admin.lifecycles.table.source.custom")}
					</Label>
				),
			},
			{
				id: "entityTypeClass",
				accessorKey: "entityTypeClass",
				header: columnLabels.entityTypeClass,
				enableSorting: true,
				minSize: 180,
				cell: ({ row }) => {
					const label = getEntityTypeClassLabel(row.original.entityTypeClass);
					return label ? (
						<p className="text-sm font-medium text-primary">{label}</p>
					) : (
						<EmptyValue />
					);
				},
			},
			{
				id: "state",
				accessorFn: (row) => (isLifecycleActive(row) ? "active" : "draft"),
				header: columnLabels.state,
				enableSorting: true,
				minSize: 120,
				cell: ({ row }) => {
					const active = isLifecycleActive(row.original);
					return (
						<BadgeWithDot
							type="modern"
							size="md"
							color={active ? "success" : "gray"}
						>
							{active
								? t("admin.lifecycles.table.state.active")
								: t("admin.lifecycles.table.state.draft")}
						</BadgeWithDot>
					);
				},
			},
			{
				id: "id",
				accessorKey: "id",
				header: columnLabels.id,
				enableSorting: true,
				minSize: 200,
				cell: ({ row }) => {
					return row.original.id ? (
						<CopyableId value={row.original.id} />
					) : (
						<EmptyValue />
					);
				},
			},
			{
				id: "actions",
				header: getActionsColumnHeader(t),
				enableSorting: false,
				enableHiding: false,
				size: 56,
				meta: {
					disableColumnReorder: true,
					rowFullViewAction: (row: unknown) =>
						navigateFullPage((row as StatusLifecycleDisplay).id),
					cellProps: {
						className: "w-14 px-2",
						"data-suppress-row-click": true,
					},
				},
				cell: ({ row }) => {
					const lc = row.original;
					return (
						<div className="flex justify-end" data-suppress-row-click>
							<Dropdown.Root>
								<Dropdown.DotsButton
									data-testid="lifecycle-table-row-actions-btn"
									aria-label={t("admin.lifecycles.table.actionsMenu.ariaLabel")}
								/>
								<Dropdown.Popover className="w-min">
									<Dropdown.Menu>
										<Dropdown.Item
											id="view"
											label={t(
												"admin.lifecycles.table.actions.viewFullDetails",
											)}
											icon={Expand03}
											onAction={() => navigateFullPage(lc.id)}
										/>
										<Dropdown.Item
											id="duplicate"
											label={t("admin.lifecycles.table.actions.duplicate")}
											icon={Copy01}
											isDisabled
										/>
										<LifecycleDeleteAction
											lc={lc}
											onDelete={() =>
												setSelectedLifecycle({
													id: lc.id,
													name: statusLifecycleDisplayLabel(lc),
												})
											}
										/>
									</Dropdown.Menu>
								</Dropdown.Popover>
							</Dropdown.Root>
						</div>
					);
				},
			},
		],
		[columnLabels, navigateFullPage, t],
	);

	const loadError =
		pageQuery.error ?? countQuery.error ?? unfilteredCountQuery.error;

	if (isFullRoute) {
		return <Outlet />;
	}

	if (loadError) {
		return (
			<div className="space-y-0">
				<div className="flex flex-col items-center justify-center py-12">
					<div className="text-center">
						<h3 className="text-sm font-medium text-error">
							{t("admin.lifecycles.errors.failedToLoad")}
						</h3>
						<p className="text-sm text-tertiary mt-1">{loadError.message}</p>
						<Button
							size="sm"
							color="primary"
							onClick={handleRefresh}
							className="mt-3"
						>
							{t("common.actions.tryAgain")}
						</Button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<>
			<PageLayout
				header={
					<PageHeader
						type="landing"
						title={t("admin.lifecycles.table.title")}
						description={t("admin.lifecycles.table.orgDescription")}
						actions={
							canCreateLifecycle.data
								? [
										{
											label: t(
												"admin.lifecycles.table.actions.createLifecycle",
											),
											color: "accent",
											onPress: () => setIsOpen(true),
											testId: "lifecycle-table-create-btn",
										},
									]
								: undefined
						}
						overflowControl={<div ref={setTableOptionsHost} />}
						overflowAriaLabel={t("common.tableActions.options")}
					/>
				}
			>
				<CustomTableWrapper<StatusLifecycleDisplay>
					tableKey={TABLE_KEY}
					data={data}
					columns={lifecycleColumns}
					showHeaderPadding={false}
					zebraStriping
					isLoading={pageQuery.isLoading || countQuery.isLoading}
					isFetching={
						pageQuery.isFetching ||
						countQuery.isFetching ||
						unfilteredCountQuery.isFetching
					}
					isRefreshing={isRefreshing}
					onRefresh={handleRefresh}
					paginationMode="server"
					externalPaginationState={{ pageSize, pageIndex }}
					onPaginationChange={handlePaginationChange}
					pageCount={pageCount}
					rowCount={countQuery.data}
					totalRowCount={unfilteredCountQuery.data}
					sortFieldMapping={sortFieldMapping}
					defaultSortField="name"
					onSortChange={onSortChange}
					filterPlaceholder={t("admin.lifecycles.table.search.placeholder")}
					filterValue={searchTerm}
					onDebouncedFilterChange={(s) => {
						setSearchTerm(s);
						setPageIndex(0);
						saveSearchTerm(s);
					}}
					onExportClick={handleExport}
					isExportLoading={exportMutation.isPending}
					enableTableActions
					filterLayout="bar"
					filterBarType="landing"
					filterBarClassName="pt-0"
					tableOptionsPortalTarget={tableOptionsHost}
					hasActiveFilters={searchTrimmed.length > 0}
					enableColumnReordering
					enableColumnResizing
					onRowClick={handleRowClick}
					getRowId={(row) => row.id}
					initialSortingState={tableSettings.sorting}
					initialColumnOrder={normalizeColumnOrder(tableSettings.columnOrder)}
					initialColumnVisibility={tableSettings.columnVisibility}
					settingsLoaded={settingsLoaded}
					onSortingStateChange={saveSorting}
					onColumnOrderChange={(order) => {
						saveColumnOrder(normalizeColumnOrder(order));
					}}
					onColumnVisibilityChange={saveColumnVisibility}
					initialSorting={DEFAULT_SORTING}
				/>

				{!pageQuery.isLoading &&
				!pageQuery.isFetching &&
				!countQuery.isFetching &&
				(countQuery.data ?? 0) === 0 &&
				searchTrimmed.length === 0 ? (
					<div className="flex justify-center py-12 text-gray-500">
						{t("admin.lifecycles.table.empty.noLifecycles")}
					</div>
				) : null}

				{!pageQuery.isLoading &&
				!pageQuery.isFetching &&
				!countQuery.isFetching &&
				(countQuery.data ?? 0) === 0 &&
				searchTrimmed.length > 0 ? (
					<TableSearchEmptyState titleKey="admin.lifecycles.table.empty.noSearchResults" />
				) : null}
			</PageLayout>

			{canCreateLifecycle.data ? (
				<CreateLifecycleModal
					isOpen={isOpen}
					setIsOpen={setIsOpen}
					handleClose={() => {
						setIsOpen(false);
					}}
					mode="organization"
					organizationId={orgId}
				/>
			) : null}
			<DeleteLifecycleModal
				isOpen={selectedLifecycle !== null}
				setIsOpen={(open) => {
					if (!open) setSelectedLifecycle(null);
				}}
				handleClose={() => setSelectedLifecycle(null)}
				onDeleteSuccess={() => setSelectedLifecycle(null)}
				lifecycleId={selectedLifecycle?.id ?? ""}
				lifecycleName={selectedLifecycle?.name ?? ""}
				organizationId={isPlatformAdmin ? orgId : undefined}
				scope="organization"
			/>
			<Outlet />
		</>
	);
}
